import { useCallback, useEffect, useState } from "react";
import { Camera, Check, Link2, Rocket, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import ConnectEbayBanner from "@/pages/ConnectEbayBanner";

export const ONBOARDING_EVENT = "snaplist:onboarding-refresh";
export const refreshOnboarding = () => window.dispatchEvent(new Event(ONBOARDING_EVENT));

const openEbaySettings = () => document.querySelector('[data-testid="open-ebay-settings"]')?.click();
const openPhotoPicker = () => {
  document.querySelector('[data-testid="uploader-dropzone"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
  document.querySelector('[data-testid="photo-file-input"]')?.click();
};
const scrollToPublish = () =>
  document.querySelector('[data-testid="publish-ebay-button"]')?.scrollIntoView({ behavior: "smooth", block: "center" });

const checkPolicies = async () => {
  const t = toast.loading("Checking your eBay shipping, payment & return policies…");
  try {
    await api.post("/ebay/policies/auto");
    toast.success("eBay policies found — you're ready to post", { id: t });
    refreshOnboarding();
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Couldn't check policies", { id: t, duration: 9000 });
    openEbaySettings();
    setTimeout(() => document.querySelector('[data-testid="ebay-policies-section"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 600);
  }
};

const STEPS = [
  { key: "connect_ebay", title: "Connect eBay", hint: "Sign in to eBay once so SnapList can list for you.", icon: Link2, cta: "Connect", action: openEbaySettings },
  { key: "ebay_policies", title: "eBay policies", hint: "Shipping, payment & return policies eBay needs on every listing.", icon: ShieldCheck, cta: "Check / set up", action: checkPolicies },
  { key: "snap_photo", title: "Snap a photo", hint: "Add photos of your item — AI writes the listing.", icon: Camera, cta: "Add photos", action: openPhotoPicker },
  { key: "publish", title: "Publish", hint: "Review the draft and tap Publish on eBay.", icon: Rocket, cta: "Show me", action: scrollToPublish },
];

function Step({ step, n, done, current }) {
  const Icon = step.icon;
  return (
    <li className={`flex items-start gap-3 rounded-xl border p-3 transition-colors ${done ? "border-emerald-200 bg-emerald-50/60" : current ? "border-[#0053DA]/30 bg-white shadow-sm" : "border-slate-200 bg-white/60"}`}
      data-testid={`onboarding-step-${step.key}`} data-done={done ? "true" : "false"}>
      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${done ? "bg-emerald-500 text-white" : current ? "bg-[#0053DA] text-white" : "bg-slate-100 text-slate-500"}`}>
        {done ? <Check className="h-4 w-4" /> : n}
      </span>
      <div className="min-w-0 flex-1">
        <p className={`flex items-center gap-1.5 text-sm font-semibold ${done ? "text-emerald-800 line-through decoration-emerald-400" : "text-slate-900"}`}>
          <Icon className="h-3.5 w-3.5" /> {step.title}
        </p>
        <p className="text-xs text-slate-600">{step.hint}</p>
        {current ? (
          <button type="button" onClick={step.action} data-testid={`onboarding-cta-${step.key}`}
            className="mt-2 rounded-full bg-[#0053DA] px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-[#0043B0]">
            {step.cta}
          </button>
        ) : null}
      </div>
    </li>
  );
}

export default function OnboardingChecklist() {
  const [state, setState] = useState(null);

  const load = useCallback(() => {
    api.get("/onboarding").then(({ data }) => setState(data)).catch(() => setState({ dismissed: true }));
  }, []);

  useEffect(() => {
    load();
    window.addEventListener(ONBOARDING_EVENT, load);
    window.addEventListener("focus", load);
    return () => {
      window.removeEventListener(ONBOARDING_EVENT, load);
      window.removeEventListener("focus", load);
    };
  }, [load]);

  if (!state) return null;
  if (state.dismissed || state.completed) return <ConnectEbayBanner />;

  const doneCount = STEPS.filter((s) => state.steps[s.key]).length;
  const currentKey = STEPS.find((s) => !state.steps[s.key])?.key;
  const skip = () => {
    setState((s) => ({ ...s, dismissed: true }));
    api.post("/onboarding/dismiss").catch(() => {});
  };

  return (
    <section className="mb-6 rounded-2xl border border-[#0053DA]/15 bg-gradient-to-br from-[#0053DA]/5 to-white p-5" data-testid="onboarding-checklist">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-[#0053DA]">Welcome to SnapList</p>
          <h2 className="text-lg font-semibold text-slate-900">Your first listing in {STEPS.length} steps</h2>
          <p className="text-xs text-slate-500" data-testid="onboarding-progress">{doneCount} of {STEPS.length} done</p>
        </div>
        <button type="button" onClick={skip} data-testid="onboarding-skip"
          className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-slate-500 hover:bg-white hover:text-slate-800">
          Skip <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="mb-4 h-1.5 overflow-hidden rounded-full bg-slate-200">
        <div className="h-full rounded-full bg-[#0053DA] transition-[width] duration-500" style={{ width: `${(doneCount / 3) * 100}%` }} />
      </div>
      <ol className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {STEPS.map((s, i) => <Step key={s.key} step={s} n={i + 1} done={state.steps[s.key]} current={s.key === currentKey} />)}
      </ol>
    </section>
  );
}
